"use client";

import { useUser } from "@clerk/tanstack-react-start";
import * as Sentry from "@sentry/tanstackstart-react";
import React, {
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import type { WrappedPrivateKey } from "./crypto";
import { KeyManager } from "./key-manager";

/** LocalStorage key prefix — scoped per Clerk user to prevent cross-user leakage */
const SESSION_KEY_PREFIX = "keySession";

/** Build the user-scoped localStorage key. Falls back to unscoped for anonymous. */
function sessionKey(clerkUserId: string | undefined): string {
  return clerkUserId
    ? `${SESSION_KEY_PREFIX}:${clerkUserId}`
    : SESSION_KEY_PREFIX;
}

/** Persisted data in localStorage (no secrets — only the wrapped key blob) */
interface SessionData {
  wrappedPrivateKey: WrappedPrivateKey | null;
  salt: string | null;
}

/**
 * Life Design milestones written before their titles were encrypted still
 * hold plain text. Encrypt them in the background after each unlock, so the
 * unlock never waits on it. Older coaching records stay with the PIN-setup
 * migration: their coach could not read them once only the client holds
 * their keys.
 */
async function encryptRemainingPlaintext(manager: KeyManager): Promise<void> {
  try {
    const { migrateUserData } = await import("./migrate-user-data");
    await migrateUserData(manager.getPrivateKey(), { goalStepsOnly: true });
  } catch (error) {
    console.error("Data migration failed (non-fatal):", error);
    Sentry.captureException(error, { tags: { surface: "pin_data_migration" } });
  }
}

/**
 * KeyContext interface for managing encryption keys.
 *
 * Provides access to KeyManager instance and unlock/lock/setup functionality.
 */
export interface KeyContext {
  /** The active KeyManager instance (null if not unlocked) */
  keyManager: KeyManager | null;
  /** Whether encryption is currently unlocked */
  isUnlocked: boolean;
  /** Whether wrapped keys are available (user has set up encryption) */
  hasKeys: boolean;
  /** Set wrapped key data (after fetching from server or setup) */
  setKeys: (wrappedPrivateKey: WrappedPrivateKey, salt: string) => void;
  /**
   * Unlock encryption with PIN. Pass `wrappedKey`/`salt` when unlocking keys
   * just produced by `setKeys` in the same call — state from that call hasn't
   * re-rendered yet, so reading it here would see the pre-setup null values.
   */
  unlock: (
    pin: string,
    wrappedKey?: WrappedPrivateKey,
    salt?: string,
  ) => Promise<void>;
  /** Lock encryption, clearing keys from memory */
  lock: () => void;
}

const KeyContextInstance = React.createContext<KeyContext | null>(null);

/**
 * KeyProvider provides encryption key management to the app.
 *
 * Stores wrapped key blob in localStorage so returning users can unlock
 * without re-fetching from server.
 */
export function KeyProvider({
  children,
}: {
  children: ReactNode;
}): React.ReactNode {
  const { user, isLoaded } = useUser();
  const clerkUserId = user?.id;
  const prevClerkUserIdRef = useRef<string | undefined>(undefined);

  const [keyManager, setKeyManager] = useState<KeyManager | null>(null);
  const [wrappedPrivateKey, setWrappedPrivateKey] =
    useState<WrappedPrivateKey | null>(null);
  const [salt, setSalt] = useState<string | null>(null);

  // Load wrapped key from user-scoped localStorage.
  // Re-runs when the Clerk user changes (login/logout/switch).
  useEffect(() => {
    if (!isLoaded) return;

    // If user changed, clear in-memory state first
    if (prevClerkUserIdRef.current !== clerkUserId) {
      if (prevClerkUserIdRef.current !== undefined) {
        keyManager?.lock();
        setKeyManager(null);
        setWrappedPrivateKey(null);
        setSalt(null);
      }
      prevClerkUserIdRef.current = clerkUserId;
    }

    try {
      const key = sessionKey(clerkUserId);
      const stored = localStorage.getItem(key);

      // Clean up legacy unscoped key — it may belong to a different user
      // and caused cross-user key leakage. The EncryptionGate will re-fetch
      // keys from the server for the current user.
      if (clerkUserId) {
        localStorage.removeItem(SESSION_KEY_PREFIX);
      }

      if (!stored) return;
      const session: SessionData = JSON.parse(stored);
      if (session.wrappedPrivateKey && session.salt) {
        setWrappedPrivateKey(session.wrappedPrivateKey);
        setSalt(session.salt);
      }
    } catch {
      // Ignore parse errors, start fresh
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, clerkUserId]);

  // Persist wrapped key to user-scoped localStorage
  useEffect(() => {
    if (!isLoaded) return;
    const session: SessionData = { wrappedPrivateKey, salt };
    try {
      localStorage.setItem(sessionKey(clerkUserId), JSON.stringify(session));
    } catch {
      // Ignore storage errors (e.g., private browsing)
    }
  }, [wrappedPrivateKey, salt, isLoaded, clerkUserId]);

  const lock = useCallback(() => {
    keyManager?.lock();
    setKeyManager(null);
  }, [keyManager]);

  const setKeys = useCallback((wpk: WrappedPrivateKey, s: string) => {
    setWrappedPrivateKey(wpk);
    setSalt(s);
  }, []);

  const unlock = useCallback(
    async (
      pin: string,
      wrappedKeyOverride?: WrappedPrivateKey,
      saltOverride?: string,
    ) => {
      const keyToUnlock = wrappedKeyOverride ?? wrappedPrivateKey;
      const saltToUnlock = saltOverride ?? salt;

      if (!keyToUnlock || !saltToUnlock) {
        throw new Error(
          "Encryption keys not set up. Please set up a PIN first.",
        );
      }
      const manager = await KeyManager.unlock(keyToUnlock, saltToUnlock, pin);
      setKeyManager(manager);
      void encryptRemainingPlaintext(manager);
    },
    [wrappedPrivateKey, salt],
  );

  const isUnlocked = keyManager !== null;
  const hasKeys = wrappedPrivateKey !== null && salt !== null;

  const value: KeyContext = {
    keyManager,
    isUnlocked,
    hasKeys,
    setKeys,
    unlock,
    lock,
  };

  return (
    <KeyContextInstance.Provider value={value}>
      {children}
    </KeyContextInstance.Provider>
  );
}

/** Hook to access KeyContext. Must be within a KeyProvider. */
export function useKeyManager(): KeyContext {
  const context = useContext(KeyContextInstance);
  if (!context) {
    throw new Error("useKeyManager must be used within a KeyProvider");
  }
  return context;
}

/** Convenience hook — returns KeyManager if unlocked, null otherwise. */
export function useUnlockedKey(): KeyManager | null {
  const { keyManager } = useKeyManager();
  return keyManager;
}
